/**
 * Gespraeche.
 * Grundlage: 05_dialer.md, 06_telephony_sip_inbound.md, 09_billing_payments.md
 */

export const CALL_DIRECTION = {
  OUTBOUND: 'outbound',
  INBOUND: 'inbound',
} as const;

export type CallDirection = (typeof CALL_DIRECTION)[keyof typeof CALL_DIRECTION];

export const CALL_INITIATION = {
  MANUAL: 'manual',
  AUTODIAL: 'autodial',
} as const;

export type CallInitiation = (typeof CALL_INITIATION)[keyof typeof CALL_INITIATION];

/**
 * Lebenszyklus eines Gespraechs, serverseitig gefuehrt.
 * Der Browser zeigt diesen Zustand nur an.
 */
export const CALL_STATUS = {
  /** Anwahl laeuft, der Kunde klingelt noch nicht. */
  DIALING: 'dialing',
  RINGING: 'ringing',
  /**
   * Der Kunde hat abgenommen, es ist aber noch kein Caller zugeteilt.
   *
   * Entsteht nur beim Autodial: die Anwahl laeuft ohne Caller, und zwischen
   * dem Abheben und der Zuteilung liegt die Warteschleife. Diese Zeit ist
   * ausdruecklich nicht abrechenbar (09_billing_payments.md).
   */
  WAITING: 'waiting',
  /** Caller und Kunde sind in derselben Audio-Bruecke. */
  CONNECTED: 'connected',
  /** Der Browser des Callers fehlt gerade; der Kundenkanal bleibt bestehen. */
  RECONNECTING: 'reconnecting',
  ENDED: 'ended',
} as const;

export type CallStatus = (typeof CALL_STATUS)[keyof typeof CALL_STATUS];

/** Technische Ergebnisse laut 05_dialer.md. */
export const TECHNICAL_RESULT = {
  CONNECTED: 'connected',
  NO_ANSWER: 'no_answer',
  BUSY: 'busy',
  VOICEMAIL: 'voicemail',
  INVALID_NUMBER: 'invalid_number',
  TECHNICAL_ERROR: 'technical_error',
  /** Der Caller hat vor dem Verbinden abgebrochen. */
  CANCELLED: 'cancelled',
} as const;

export type TechnicalResult = (typeof TECHNICAL_RESULT)[keyof typeof TECHNICAL_RESULT];

/** Vertriebsergebnis nach einem verbundenen Gespraech. */
export const SALES_RESULT = {
  SUCCESSFUL: 'successful',
  CALLBACK: 'callback',
  NOT_INTERESTED: 'not_interested',
} as const;

export type SalesResult = (typeof SALES_RESULT)[keyof typeof SALES_RESULT];

export const SALES_RESULT_VALUES = Object.values(SALES_RESULT);

export function isSalesResult(value: unknown): value is SalesResult {
  return typeof value === 'string' && (SALES_RESULT_VALUES as string[]).includes(value);
}

/**
 * Erlaubte Zustandsuebergaenge.
 *
 * Asterisk liefert Ereignisse gelegentlich doppelt oder in unerwarteter
 * Reihenfolge. Ein Uebergang, der hier nicht steht, wird verworfen - ein
 * bereits beendetes Gespraech laesst sich damit nicht wieder oeffnen
 * (13_architecture.md: Idempotenz).
 */
export const ALLOWED_CALL_TRANSITIONS: Record<CallStatus, readonly CallStatus[]> = {
  [CALL_STATUS.DIALING]: [
    CALL_STATUS.RINGING,
    CALL_STATUS.WAITING,
    CALL_STATUS.CONNECTED,
    CALL_STATUS.ENDED,
  ],
  [CALL_STATUS.RINGING]: [CALL_STATUS.WAITING, CALL_STATUS.CONNECTED, CALL_STATUS.ENDED],
  [CALL_STATUS.WAITING]: [CALL_STATUS.CONNECTED, CALL_STATUS.ENDED],
  [CALL_STATUS.CONNECTED]: [CALL_STATUS.RECONNECTING, CALL_STATUS.ENDED],
  [CALL_STATUS.RECONNECTING]: [CALL_STATUS.CONNECTED, CALL_STATUS.ENDED],
  [CALL_STATUS.ENDED]: [],
};

/**
 * Ergebnisse, nach denen erneut gewaehlt werden darf (05_dialer.md).
 *
 * Keine Antwort, Besetzt und Mailbox teilen sich denselben Zaehler. Bei einer
 * ungueltigen Nummer wird nie erneut gewaehlt, und ein technischer Fehler
 * liegt an uns, nicht am Kunden - auch dort kein automatischer zweiter
 * Versuch.
 */
export const RETRYABLE_RESULTS: readonly TechnicalResult[] = [
  TECHNICAL_RESULT.NO_ANSWER,
  TECHNICAL_RESULT.BUSY,
  TECHNICAL_RESULT.VOICEMAIL,
];

export function isRetryableResult(result: TechnicalResult | null): boolean {
  return result !== null && RETRYABLE_RESULTS.includes(result);
}

export function canTransition(from: CallStatus, to: CallStatus): boolean {
  return ALLOWED_CALL_TRANSITIONS[from].includes(to);
}

/**
 * Abrechenbare Minuten: pro Gespraech auf volle Minuten aufgerundet
 * (09_billing_payments.md).
 *
 * 3 Sekunden ergeben 1 Minute, 61 Sekunden ergeben 2 Minuten. Wer sofort nach
 * dem Verbinden auflegt, zahlt 1 Minute. Ein nie verbundenes Gespraech kostet
 * nichts.
 */
export function billableMinutes(connectedSeconds: number): number {
  if (connectedSeconds <= 0) {
    return 0;
  }
  return Math.ceil(connectedSeconds / 60);
}

/** Wie der Caller das Gespraech gerade sieht. */
export interface CallView {
  id: string;
  campaignId: string;
  contactId: string | null;
  phoneNumber: string;
  status: CallStatus;
  /** Serverseitiger Zeitstempel - die Anzeige rechnet daraus die Dauer. */
  connectedAt: string | null;
  startedAt: string;
  muted: boolean;
  technicalResult: TechnicalResult | null;
  /** Nur bei einem verbundenen Gespraech mit Datensatz noetig. */
  requiresWrapUp: boolean;
}
